import { useEffect, useState, type InputHTMLAttributes } from 'react';
import { useForm, type FieldErrors, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { CHECK_TARGET_SCHEMAS, type MonitoringCheck } from '@bekebi/contracts/api';
import { CHECK_KINDS, CHECK_KIND_SERVICE_TYPE, MONITORING, type CheckKind } from '@bekebi/domain';
import { Button } from '../../components/button';
import { ErrorState, LoadingState } from '../../components/states';
import { useServices } from '../services/api';
import { CHECK_KIND_TITLES } from '../services/format';
import { useCreateCheck, useUpdateCheck } from './api';
import { NEXT_RESULT_NOTE } from './present';

/**
 * Check configuration forms. The fields are exactly those the API accepts: service and kind (when
 * adding), the kind's target, the interval and — when adding — whether it starts enabled. Targets
 * are validated with the API's own schemas (CHECK_TARGET_SCHEMAS); the API validates again, applies
 * its destination allow-list and has the final word. No credentials are part of any target.
 */

const FIELD_MESSAGES: Record<string, string> = {
  baseUrl: 'Enter an https:// address without a user name, password, query or fragment.',
  host: 'Enter a host name (for example mail.example.org).',
  port: 'Enter a port from 1 to 65535.',
  security: 'Choose how the connection is secured.',
};

const fieldsSchema = z.object({
  baseUrl: z.string().trim(),
  host: z.string().trim(),
  port: z.string().trim(),
  security: z.enum(['implicit', 'starttls']),
  intervalSeconds: z
    .string()
    .trim()
    .refine(
      (v) =>
        /^\d+$/.test(v) &&
        Number(v) >= MONITORING.minIntervalSeconds &&
        Number(v) <= MONITORING.maxIntervalSeconds,
      {
        message: `Enter a whole number of seconds from ${MONITORING.minIntervalSeconds} to ${MONITORING.maxIntervalSeconds}.`,
      },
    ),
});
type Fields = z.infer<typeof fieldsSchema>;

/** The register function of either form, narrowed to the shared fields. */
type RegisterFn = (name: keyof Fields) => UseFormRegisterReturn;

/** The target object for a kind, built from the form. */
function buildTarget(kind: CheckKind, v: Fields): Record<string, unknown> {
  switch (kind) {
    case 'website_https':
      return {};
    case 'nextcloud_status':
      return { baseUrl: v.baseUrl };
    case 'smtp_tls':
      return { host: v.host, port: Number(v.port), security: v.security };
    case 'imap_tls':
      return { host: v.host, port: Number(v.port) };
    case 'mail_dns':
      // The form's host field holds the mail domain; Mailu's DKIM selector is "dkim".
      return { domain: v.host, dkimSelector: 'dkim' };
  }
}

/** Adds the API's target-schema problems to the form, on the right field. */
function checkTarget(kind: CheckKind, v: Fields, ctx: z.RefinementCtx) {
  const parsed = CHECK_TARGET_SCHEMAS[kind].safeParse(buildTarget(kind, v));
  if (parsed.success) return;
  for (const issue of parsed.error.issues) {
    const raw = String(issue.path[0] ?? 'host');
    const field = raw === 'domain' || raw === 'dkimSelector' ? 'host' : raw;
    ctx.addIssue({
      code: 'custom',
      path: [field],
      message: FIELD_MESSAGES[field] ?? issue.message,
    });
  }
}

function TargetFields({
  kind,
  register,
  errors,
}: {
  kind: CheckKind;
  register: RegisterFn;
  errors: FieldErrors<Fields>;
}) {
  if (kind === 'website_https') {
    return (
      <p className="text-sm text-muted">
        This check uses the Website&rsquo;s public address, set on the service itself.
      </p>
    );
  }
  if (kind === 'mail_dns') {
    return (
      <TextField
        id="check-host"
        label="Mail domain"
        hint="The domain the Mail Server sends for, e.g. example.org. DKIM selector: dkim (Mailu's default). Public DNS lookups only."
        error={errors.host?.message}
        inputProps={{ ...register('host'), autoComplete: 'off', spellCheck: false }}
      />
    );
  }
  if (kind === 'nextcloud_status') {
    return (
      <TextField
        id="check-base-url"
        label="Cloud Server address"
        hint="The application's https:// address; the check reads /status.php there."
        error={errors.baseUrl?.message}
        inputProps={{
          ...register('baseUrl'),
          type: 'url',
          inputMode: 'url',
          placeholder: 'https://',
        }}
      />
    );
  }
  return (
    <>
      <TextField
        id="check-host"
        label="Host"
        error={errors.host?.message}
        inputProps={{ ...register('host'), autoComplete: 'off', spellCheck: false }}
      />
      <TextField
        id="check-port"
        label="Port"
        error={errors.port?.message}
        inputProps={{ ...register('port'), inputMode: 'numeric' }}
      />
      {kind === 'smtp_tls' ? (
        <fieldset>
          <legend className="text-sm font-medium text-fg-2">Connection security</legend>
          <div className="mt-2 space-y-1">
            <label className="flex min-h-10 items-center gap-3 text-sm">
              <input type="radio" value="implicit" {...register('security')} className="size-4" />
              Implicit TLS (for example port 465)
            </label>
            <label className="flex min-h-10 items-center gap-3 text-sm">
              <input type="radio" value="starttls" {...register('security')} className="size-4" />
              STARTTLS (for example port 587)
            </label>
          </div>
        </fieldset>
      ) : null}
    </>
  );
}

function TextField({
  id,
  label,
  hint,
  error,
  inputProps,
}: {
  id: string;
  label: string;
  hint?: string;
  error: string | undefined;
  inputProps: InputHTMLAttributes<HTMLInputElement> & { name: string };
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-fg-2">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        className="mt-1 block w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 py-2 font-mono text-sm"
        {...inputProps}
      />
      {hint ? (
        <p id={hintId} className="mt-1 text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="mt-1 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function IntervalField({ register, error }: { register: RegisterFn; error?: string }) {
  return (
    <TextField
      id="check-interval"
      label="Interval (seconds)"
      hint={`How often the check runs: ${MONITORING.minIntervalSeconds}–${MONITORING.maxIntervalSeconds} seconds (default ${MONITORING.defaultIntervalSeconds}).`}
      error={error}
      inputProps={{ ...register('intervalSeconds'), inputMode: 'numeric' }}
    />
  );
}

// ---------------------------------------------------------------------------------------------
// Add check (/app/monitoring/new, monitoring.manage)
// ---------------------------------------------------------------------------------------------

const createSchema = fieldsSchema
  .extend({
    serviceId: z.string().min(1, 'Choose a service.'),
    kind: z.union([z.enum(CHECK_KINDS), z.literal('')]),
    enabled: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.kind === '') {
      ctx.addIssue({ code: 'custom', path: ['kind'], message: 'Choose what to check.' });
      return;
    }
    checkTarget(v.kind, v, ctx);
  });
type CreateFields = z.infer<typeof createSchema>;

export function CheckCreatePage() {
  const services = useServices();
  const create = useCreateCheck();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateFields>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      serviceId: searchParams.get('service') ?? '',
      kind: '',
      baseUrl: '',
      host: '',
      port: '',
      security: 'implicit',
      intervalSeconds: String(MONITORING.defaultIntervalSeconds),
      enabled: true,
    },
  });
  const serviceId = watch('serviceId');
  const kind = watch('kind');
  const service = services.data?.find((s) => s.id === serviceId);
  const kinds = service
    ? CHECK_KINDS.filter((k) => CHECK_KIND_SERVICE_TYPE[k] === service.type)
    : [];

  const onSubmit = handleSubmit((v) => {
    if (v.kind === '') return;
    create.mutate(
      {
        serviceId: v.serviceId,
        kind: v.kind,
        target: buildTarget(v.kind, v),
        intervalSeconds: Number(v.intervalSeconds),
        enabled: v.enabled,
      } as Parameters<typeof create.mutate>[0],
      {
        onSuccess: (check) =>
          void navigate(`/app/monitoring/${check.id}`, { state: { created: true } }),
      },
    );
  });

  if (services.isPending) return <LoadingState label="Loading services…" />;
  if (services.isError) {
    return (
      <ErrorState
        title="Services could not be loaded"
        message={services.error.message}
        onRetry={() => void services.refetch()}
      />
    );
  }

  return (
    <div className="max-w-xl">
      <p className="text-sm">
        <Link
          to="/app/monitoring"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Monitoring
        </Link>
      </p>
      <h1 className="mt-2">Add check</h1>
      <form onSubmit={onSubmit} noValidate aria-label="Add check" className="mt-6 space-y-5">
        <div>
          <label htmlFor="check-service" className="block text-sm font-medium text-fg-2">
            Service
          </label>
          <select
            id="check-service"
            className="mt-1 block min-h-10 w-full rounded-md border border-line-strong bg-surface px-3 text-sm"
            {...register('serviceId', { onChange: () => setValue('kind', '') })}
          >
            <option value="">Choose a service…</option>
            {services.data.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.type === 'website' && s.accessUrl ? ` (${s.accessUrl})` : ''}
              </option>
            ))}
          </select>
          {errors.serviceId ? (
            <p className="mt-1 text-sm text-danger">{errors.serviceId.message}</p>
          ) : null}
        </div>

        {service ? (
          kinds.length === 0 ? (
            <p className="text-sm text-muted">No kind of check exists for a {service.name} yet.</p>
          ) : (
            <fieldset>
              <legend className="text-sm font-medium text-fg-2">What to check</legend>
              <div className="mt-2 space-y-1">
                {kinds.map((k) => (
                  <label key={k} className="flex min-h-10 items-center gap-3 text-sm">
                    <input type="radio" value={k} {...register('kind')} className="size-4" />
                    {CHECK_KIND_TITLES[k]}
                  </label>
                ))}
              </div>
              {errors.kind ? (
                <p className="mt-1 text-sm text-danger">{errors.kind.message}</p>
              ) : null}
            </fieldset>
          )
        ) : null}

        {kind ? <TargetFields kind={kind} register={register} errors={errors} /> : null}

        {kind ? (
          <IntervalField register={register} error={errors.intervalSeconds?.message} />
        ) : null}

        {kind ? (
          <label className="flex min-h-10 items-center gap-3 text-sm">
            <input type="checkbox" {...register('enabled')} className="size-4" />
            Start the check straight away (enabled)
          </label>
        ) : null}

        {create.isError ? (
          <p role="alert" className="text-sm text-danger">
            {create.error.message}
          </p>
        ) : null}

        <div className="flex gap-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add check'}
          </Button>
          <Link
            to="/app/monitoring"
            className="inline-flex min-h-10 items-center rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-fg hover:bg-raised"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Edit check (target + interval; monitoring.manage). Enable/disable is a separate action.
// ---------------------------------------------------------------------------------------------

/** The form's values for a check, as the API returned it. */
function valuesFor(check: MonitoringCheck): Fields {
  const t = check.target as Record<string, unknown>;
  return {
    baseUrl: typeof t.baseUrl === 'string' ? t.baseUrl : '',
    host: typeof t.host === 'string' ? t.host : typeof t.domain === 'string' ? t.domain : '',
    port: typeof t.port === 'number' ? String(t.port) : '',
    security: t.security === 'starttls' ? 'starttls' : 'implicit',
    intervalSeconds: String(check.intervalSeconds),
  };
}

export function EditCheckForm({ check }: { check: MonitoringCheck }) {
  const update = useUpdateCheck(check.id);
  const [saved, setSaved] = useState(false);
  const editSchema = fieldsSchema.superRefine((v, ctx) => checkTarget(check.kind, v, ctx));
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Fields>({ resolver: zodResolver(editSchema), defaultValues: valuesFor(check) });

  // When the API returns a new version of the check, show its values — without re-mounting the
  // form, so the confirmation of a save stays visible. (Re-syncs on the version only.)
  const version = check.updatedAt;
  useEffect(() => {
    reset(valuesFor(check));
  }, [version]);

  const onSubmit = handleSubmit((v) => {
    setSaved(false);
    update.mutate(
      { target: buildTarget(check.kind, v), intervalSeconds: Number(v.intervalSeconds) },
      { onSuccess: () => setSaved(true) },
    );
  });

  return (
    <form onSubmit={onSubmit} noValidate aria-label="Edit check" className="space-y-4">
      <TargetFields kind={check.kind} register={register} errors={errors} />
      <IntervalField register={register} error={errors.intervalSeconds?.message} />
      <p className="text-xs text-muted">
        Changing the target clears the check&rsquo;s last result. {NEXT_RESULT_NOTE} Changing only
        the interval keeps the last result and reschedules the next run.
      </p>
      {update.isError ? (
        <p role="alert" className="text-sm text-danger">
          {update.error.message}
        </p>
      ) : null}
      {saved ? (
        <p role="status" className="text-sm text-success">
          The check was saved.
        </p>
      ) : null}
      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save changes'}
      </Button>
    </form>
  );
}
